import { AlertTriangle, ArrowRight } from 'lucide-react'
import { Button, Card, Chip, type ChipTone } from '../hybrid'
import {
  MEMORY_CLASSES,
  type MemoryClass,
  type ReadinessCheck,
  type ReadinessStatus,
  type ReadinessSummary,
} from '@agentwiki/shared'

const MEMORY_CLASS_LABELS: Record<MemoryClass, string> = {
  'project-doc': 'Project docs',
  'journal-entry': 'Journals',
  'decision-record': 'Decisions',
  'session-summary': 'Session summaries / handoffs',
  'agent-observation': 'Agent observations',
  'plan-report': 'Plan reports',
  runbook: 'Runbooks',
}

function toneForStatus(status: ReadinessStatus): ChipTone {
  if (status === 'ready') return 'ok'
  if (status === 'attention') return 'warn'
  if (status === 'blocked') return 'dgr'
  return 'mut'
}

function labelForStatus(status: ReadinessStatus) {
  if (status === 'ready') return 'ready'
  if (status === 'attention') return 'review'
  if (status === 'blocked') return 'blocked'
  return 'unknown'
}

function remediationLabel(route?: string) {
  if (route === '/agents') return 'Open Agents'
  if (route === '/search') return 'Open Search'
  if (route === '/storage') return 'Open Files'
  if (route === '/graph') return 'Open Graph'
  if (route === '/settings') return 'Open Settings'
  if (route === '/billing') return 'Open Billing'
  return 'Open surface'
}

function checkPriority(check: ReadinessCheck) {
  if (check.status === 'blocked') return 0
  if (check.status === 'attention') return 1
  if (check.status === 'unknown') return 2
  return 3
}

const HOME_READINESS_ROUTES = new Set([
  '/agents',
  '/search',
  '/storage',
  '/graph',
  '/settings',
  '/billing',
  '/doc',
])

function availableRoute(route: string | undefined, storageEnabled: boolean) {
  if (!route || !HOME_READINESS_ROUTES.has(route)) return undefined
  if (!storageEnabled && route === '/storage') return undefined
  return route
}

export function HomeReadinessCard({
  summary,
  isLoading,
  variant = 'full',
  storageEnabled = true,
}: {
  summary?: ReadinessSummary
  isLoading?: boolean
  variant?: 'full' | 'compact'
  storageEnabled?: boolean
}) {
  const isCompact = variant === 'compact'
  const score = summary?.score ?? 0
  const allChecks = summary?.sections.flatMap((section) => section.checks) ?? []
  const checks = allChecks.slice(0, isCompact ? 3 : 8)
  const remediationChecks = allChecks
    .filter((check) => check.status !== 'ready')
    .sort((a, b) => checkPriority(a) - checkPriority(b))
    .slice(0, isCompact ? 1 : 4)
  const primaryRemediation = remediationChecks[0]
  const memoryClasses = MEMORY_CLASSES.map((memoryClass) => ({
    memoryClass,
    label: MEMORY_CLASS_LABELS[memoryClass],
    count: summary?.memory.byClass[memoryClass] ?? 0,
  }))
  const topRisk = checks.find((check) => check.status === 'blocked' || check.status === 'attention')
  const compactCoverage = `${summary?.sections.length ?? 0} readiness areas · ${summary?.memory.total ?? 0} knowledge items`

  return (
    <Card className={`aw-readiness aw-readiness--${variant}`}>
      <div className="aw-ready-top">
        <div>
          <span className="aw-label">Agent readiness</span>
          <div className="aw-ready-num">{isLoading ? '…' : `${score}%`}</div>
        </div>
        <div className="aw-ready-note">
          LLM Wiki health
          <br />
          {isCompact ? compactCoverage : (topRisk?.label ?? 'checks current')}
        </div>
      </div>
      <div className="aw-meter">
        <span style={{ width: `${score}%` }} />
      </div>
      {checks.length > 0 ? (
        <>
          {isCompact ? (
            <div className="aw-readiness-compact-checks" aria-label="Top readiness checks">
              {checks.map((check) => (
                <div
                  key={check.id}
                  className="aw-readiness-compact-check"
                  aria-label={`${check.label}: ${labelForStatus(check.status)}`}
                >
                  <strong>{check.label}</strong>
                  <Chip tone={toneForStatus(check.status)}>{labelForStatus(check.status)}</Chip>
                </div>
              ))}
            </div>
          ) : (
            checks.map((check) => (
              <div key={check.id} className="aw-mini-row">
                <div>
                  <strong>{check.label}</strong>
                  <span>{check.detail}</span>
                </div>
                <Chip tone={toneForStatus(check.status)}>{labelForStatus(check.status)}</Chip>
              </div>
            ))
          )}
          {remediationChecks.length > 0 &&
            (isCompact ? (
              <div className="aw-remediation-compact">
                <span>
                  <AlertTriangle />
                  Fix next: {primaryRemediation?.label}
                </span>
                {availableRoute(primaryRemediation?.route, storageEnabled) && (
                  <Button
                    to={availableRoute(primaryRemediation?.route, storageEnabled)!}
                    size="sm"
                    variant="ghost"
                  >
                    {remediationLabel(availableRoute(primaryRemediation?.route, storageEnabled))}
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            ) : (
              <div className="aw-remediation">
                <div className="aw-remediation-head">
                  <span>
                    <AlertTriangle /> Fix next
                  </span>
                  <Chip tone="warn">{remediationChecks.length}</Chip>
                </div>
                {remediationChecks.map((check) => (
                  <div key={`fix-${check.id}`} className="aw-remediation-row">
                    <div>
                      <strong>{check.label}</strong>
                      <span>{check.detail}</span>
                    </div>
                    {availableRoute(check.route, storageEnabled) && (
                      <Button
                        to={availableRoute(check.route, storageEnabled)!}
                        size="sm"
                        variant="ghost"
                      >
                        {remediationLabel(availableRoute(check.route, storageEnabled))}
                        <ArrowRight className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            ))}
          {!isCompact && (
            <div className="aw-memory-classes" aria-label="Memory classes">
              <div className="aw-memory-classes-head">
                <span>Memory classes</span>
                <Chip tone={summary?.memory.total ? 'ok' : 'warn'}>
                  {summary?.memory.total ?? 0}
                </Chip>
              </div>
              <div className="aw-memory-class-grid">
                {memoryClasses.map((item) => (
                  <div key={item.memoryClass} className="aw-memory-class-cell">
                    <strong>{item.label}</strong>
                    <span>{item.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="aw-mini-row">
          <div>
            <strong>Readiness summary</strong>
            <span>{isLoading ? 'Loading workspace health' : 'No readiness signals yet'}</span>
          </div>
          <Chip tone="mut">{isLoading ? 'loading' : 'unknown'}</Chip>
        </div>
      )}
    </Card>
  )
}
